/**
 * Priority+ navigation model and fit computation.
 *
 * The navbar renders as many items as fit its measured width; the rest
 * collapse into a grouped overflow menu. See
 * docs/superpowers/specs/2026-07-22-priority-plus-navbar-design.md.
 */

export interface NavChild {
  label: string;
  route: string;
}

export interface NavItem {
  /** Stable id; also the data-test hook (`nav-<id>`). */
  id: string;
  label: string;
  /** Flat link target. Mutually exclusive with children. */
  route?: string;
  /** Dropdown entries. Mutually exclusive with route. */
  children?: NavChild[];
  authOnly?: boolean;
  adminOnly?: boolean;
  /** Overflow-menu group for flat links (dropdown items group under their own label). */
  menuGroup?: string;
  /** Longer label for the overflow menu (falls back to label). */
  menuLabel?: string;
}

export interface OverflowGroup {
  label: string;
  entries: NavChild[];
}

/**
 * Largest n such that the first n items — plus the More button when n < total —
 * fit into containerWidth. 0 means "menu-only": the caller replaces the
 * trailing More button with the left-side ≡ Menu button.
 */
export function fitCount(
  itemWidths: readonly number[],
  containerWidth: number,
  moreWidth: number,
  gap: number,
): number {
  const total = itemWidths.length;
  for (let n = total; n >= 1; n--) {
    let needed = gap * (n - 1);
    for (let i = 0; i < n; i++) {
      needed += itemWidths[i];
    }
    if (n < total) {
      needed += gap + moreWidth;
    }
    if (needed <= containerWidth) {
      return n;
    }
  }
  return 0;
}
